// EUDR Status Description — static bilingual content (EN/TH)
// Source: client document "Description of EUDR status" (TH & EN tabs).
// Content is static and NOT admin-editable.

export interface EudrText {
  en: string;
  th: string;
}

export interface EudrOption {
  /** Language-neutral option label as it appears in the doc, e.g. "a. No overlap with strict PA" */
  label: string;
  text: EudrText;
}

export interface EudrSubsection {
  /** Per-language subsection number, e.g. { en: "4.3.1", th: "1.1" } */
  no: EudrText;
  /** coded field name, shown as the subsection heading, e.g. "c1_indicate_deforestation_analysis_layer_used" */
  code: string;
  description?: EudrText;
  options?: EudrOption[];
  note?: EudrText;
}

export interface EudrSection {
  /** Stable, language-independent id for React keys, e.g. "c", "g1" */
  id: string;
  /** Per-language section title, e.g. { en: "4.3 Deforestation-free analysis", th: "1 Deforestation-free analysis" } */
  title?: EudrText;
  description?: EudrText;
  subsections: EudrSubsection[];
}

/** Section header shown above the block (feed into i18n or render directly). */
export const EUDR_STATUS_DESCRIPTION_TITLE: EudrText = {
  en: "Description of EUDR Status",
  th: "คำอธิบายสถานะ EUDR",
};

export const EUDR_STATUS_DESCRIPTION: EudrSection[] = [
  {
    id: "c",
    title: {
      en: "4.3 Deforestation-free analysis",
      th: "1 Deforestation-free analysis",
    },
    description: {
      en: "Data obtained from checking for deforestation or forest encroachment using Thai legal standards.",
      th: "เป็นข้อมูลที่ได้จากการตรวจสอบการตัดไม้ทำลายป่า (Deforestation) หรือการบุกรุกพื้นที่ป่าไม้ โดยใช้มาตราฐาน ตามกฎหมายของประเทศไทย",
    },
    subsections: [
      {
        no: { en: "4.3.1", th: "1.1" },
        code: "c1_indicate_deforestation_analysis_layer_used",
        description: {
          en: "List of data layers used for checking deforestation or natural encroachment. If more than 1 layer is used, they can all be listed together.",
          th: "รายชื่อของข้อมูลที่ใช้ในการตรวจสอบการตัดไม้ทำลายป่าหรือการบุกรุกธรรมชาติ ถ้ามีมากกว่า 1 ข้อมูลสามารถ ใส่รวมกันมาได้เลย",
        },
      },
      {
        no: { en: "4.3.2", th: "1.2" },
        code: "c2_deforestation_spatial_analysis_result",
        description: {
          en: "Results obtained from assessing the risk of deforestation or natural encroachment, categorized into 2 cases:",
          th: "ผลที่ได้จากการตรวจสอบความเสี่ยงของการตัดไม้ทำลายป่าหรือการบุกรุกธรรมชาติ โดยแบ่งออกได้ 2 กรณี คือ",
        },
        options: [
          {
            label: "a. No Indication of deforestation",
            text: {
              en: "No indication of deforestation or natural encroachment",
              th: "ไม่พบการตัดไม้ทำลายป่าหรือการบุกรุกธรรมชาติ",
            },
          },
          {
            label: "b. Indication of deforestation",
            text: {
              en: "Indication of deforestation or natural encroachment found",
              th: "พบการตัดไม้ทำลายป่าหรือการบุกรุกธรรมชาติ",
            },
          },
        ],
      },
      {
        no: { en: "4.3.3", th: "1.3" },
        code: "c3_mitigation_if_indication_found",
        description: {
          en: "Methods to help mitigate potential impacts in cases where deforestation is indicated, divided into 3 methods:",
          th: "วิธีการที่จะช่วยลดผลกระทบที่จะเกิดขึ้นในกรณีพบการตัดไม้ทำลายป่า โดยจะแยกได้เป็น 3 วิธี คือ",
        },
        options: [
          {
            label: "a. Secondary satellite analysis check",
            text: {
              en: "Use satellite imagery to verify the rubber plantation area",
              th: "ใช้ภาพถ่ายดาวเทียมในตรวจสอบพื้นที่สวนยาง",
            },
          },
          {
            label: "b. On-site verification with evidence",
            text: {
              en: "Conduct field surveys and gather evidence",
              th: "ใช้วิธีการสำรวจพื้นที่ภาคสนามและเก็บรวบรวมหลักฐาน",
            },
          },
          {
            label: "c. Not Applicable",
            text: { en: "No inspection conducted", th: "ไม่ได้มีการตรวจสอบ" },
          },
        ],
        note: {
          en: 'If no risk of deforestation is found in column c2_deforestation_spatial_analysis_result (No Indication), specify "NA" (Not Applicable).',
          th: "ถ้าหากในคอลัมน์ c2_deforestation_spatial_analysis_result ไม่พบความเสี่ยงของการตัดไมทำลายป่า (No Indication) ให้ระบุว่า “NA” (Not Applicable)",
        },
      },
      {
        no: { en: "4.3.4", th: "1.4" },
        code: "c4_reference_or_link_if_applicable",
        description: {
          en: 'Reference data/evidence used for the verification method specified in column c3. If c3 is not applicable, enter "Not Applicable".',
          th: "ข้อมูลหลักฐานสำหรับอ้างอิงที่ใช้ในวิธีการตรวจสอบที่ระบุไว้ในคอลัมน์ c3 ถ้าไม่มีคำตอบใน c3 ให้ตอบว่า “Not Applicable”",
        },
      },
      {
        no: { en: "4.3.5", th: "1.5" },
        code: "c5_deforestation_final_status",
        description: {
          en: "Final conclusion of the deforestation check, analyzed from the data in columns c1 – c4 and summarized in column c5. The result yields 2 possible outcomes:",
          th: "ผลสรุปจากกสารตรวจสอบการตัดไม้ทำลายป่า โดยวิเคราะห์จากข้อมูลในคอลัมน์ c1 – c4 และสรุปข้อมูล ทั้งหมดในคอลัมน์ c5 โดยผลสรุปจะออกมาได้ 2 ผลสรุป ได้แก่",
        },
        options: [
          {
            label: "a. Deforestation-free",
            text: {
              en: "No deforestation or forest land encroachment found",
              th: "ไม่พบการตัดไม้ทำลายป่าหรือการบุกรุกพื้นที่ป่าไม้",
            },
          },
          {
            label: "b. Deforestation present",
            text: {
              en: "Deforestation or forest land encroachment found",
              th: "พบการตัดไม้ทำลายป่า หรือมีการบุกรุกพื้นที่ป่าไม้",
            },
          },
        ],
      },
    ],
  },
  {
    id: "d",
    title: {
      en: "4.4 Land Use Rights",
      th: "2 Land Use Rights",
    },
    description: {
      en: "Explanation of land use rights verification in accordance with Thai laws.",
      th: "อธิบายการตรวจสอบการใช้ประโยชน์ที่ดิน ให้เป็นไปตามกฎหมายของประเทศไทย",
    },
    subsections: [
      {
        no: { en: "4.4.1", th: "2.1" },
        code: "d1_strict_protected_area_spatial_analysis_result",
        description: {
          en: "Results from checking against Strict Protected Areas (PA) according to Thai law, which include Watershed Class 1 and Class 2, National Parks, Forest Parks, and Wildlife Sanctuaries:",
          th: "ผลจากการตรวจสอบกับพื้นที่อนุรักษ์ (PA; Strict Protected Area ) ตามกฎหมายของประเทศไทย ประกอบไป ด้วย พื้นที่ลุ่มน้ำลำดับที่ 1 และลำดับที่ 2 เขตอุทยานแห่งชาติ เขตวนอุทยาน และเขตรักษาพันธุ ์สัตว์ป่า",
        },
        options: [
          {
            label: "a. No overlap with strict PA",
            text: {
              en: "Not located in a protected area (PA)",
              th: "ไม่อยู่ในพื้นที่อนุรักษ์ (PA)",
            },
          },
          {
            label: "b. Overlap with strict PA",
            text: {
              en: "Located in a protected area (PA)",
              th: "อยู่ในพื้นที่อนุรักษ์ (PA)",
            },
          },
        ],
      },
      {
        no: { en: "4.4.2", th: "2.2" },
        code: "d2_restricted_use_area_spatial_analysis_result",
        description: {
          en: "Results from checking against Restricted Use Areas (RA) according to Thai law. These are lands allowed by the government for farming/livelihood use only; they cannot be bought or sold, but can be passed down within the same family:",
          th: "ผลจากการตรวจสอบกับพื้นที่จำกัดการใช้ประโยชน์ที่ดิน (RA; Restricted use area) ตามกฎหมายของประเทศไทย เป็นพื้นที่ที่รัฐบาลอนุญาตให้ใช้ประโยชน์ในการทำกินเท่านั้น ไม่สามารถซื้อขายได้ แต่สามารถส่งมอบในครอบครัวเดียวกันได้",
        },
        options: [
          {
            label: "a. No overlap with RA",
            text: {
              en: "Not located in a restricted use area (RA)",
              th: "ไม่อยู่ในพื้นที่จำกัดการใช้ประโยชน์ที่ดิน (RA)",
            },
          },
          {
            label: "b. Overlap with RA",
            text: {
              en: "Located in a restricted use area (RA)",
              th: "อยู่ในพื้นที่จำกัดการใช้ประโยชน์ที่ดิน (RA)",
            },
          },
        ],
      },
      {
        no: { en: "4.4.3", th: "2.3" },
        code: "d3_land_tenure_or_documentation",
        description: {
          en: "Types of land title documents for the rubber plantation, categorized as:",
          th: "ประเภทของเอกสารสิทธิ์ของสวนยาง โดยแบ่งได้เป็น",
        },
        options: [
          {
            label: "a. Official land title (with evidence)",
            text: {
              en: "Land documents indicating ownership/possession (with evidence), such as N.S.2, N.S.3, and N.S.4.",
              th: "เอกสารสิทธิ์ที่แสดงถึงการครอบครองที่ดิน (มีหลักฐานยืนยัน) ได้แก่ น.ส.2, น.ส.3 และ น.ส.4",
            },
          },
          {
            label: "b. Official right of use (with evidence)",
            text: {
              en: "Land documents indicating land use rights (with evidence), such as K.S.N.3, K.S.N.5, K.T.C., K.B.K., State Land for the Poor, N.K.1, N.K.3, P.S.1, Treasury Department Land Lease Agreement, Agricultural Settlement, Forest Department Certification, K.B.K.7, S.P.K. 4-01, S.R.3, S.T.K.1, Land Use Permit, and Plantation Permit.",
              th: "เอกสารสิทธิ์ที่แสดงถึงการใช้ประโยชน์ที่ดิน (มีหลักฐานยืนยัน) ได้แก่ กสน.3, กสน.5, คทช., คบก., ที่ดินรัฐยากจน, น.ค.1, น.ค.3, ป.ส.1, สัญญาเช่าที่ดินราชพัสดุ, นิคมเกษตรกรรม, หนังสือรับรองกรมป่าไม้, คบก.7, ส.ป.ก.4-01, ส.ร.3, สทก.1, หนังสืออนุญาตให้ใช้ประโยชน์ที่ดิน และหนังสืออนุญาติให้ทำการปลูกสร้างสวน",
            },
          },
          {
            label: "c. Local government documentation (with evidence)",
            text: {
              en: "Land possession documents issued by local government entities (with evidence), such as P.B.T. 5.",
              th: "เอกสารสิทธิ์การครอบครองที่ดินที่ออกโดยส่วนท้องถิ่น (มีหลักฐานยืนยัน) ได้แก่ ภ.ท.บ.5",
            },
          },
          {
            label: "d. Official land title (indicative)",
            text: {
              en: "Land documents indicating ownership/possession (indicative data).",
              th: "เอกสารสิทธิ์ที่แสดงถึงการครอบครองที่ดิน (มีข้อมูลบ่งชี้)",
            },
          },
          {
            label: "e. Official right of use (indicative)",
            text: {
              en: "Land documents indicating land use rights (indicative data), such as N.S.5, S.2/27, S.K.1, and S.P.K. 5-03.",
              th: "เอกสารสิทธิ์ที่แสดงถึงการใช้ประโยชน์ที่ดิน (มีข้อมูลบ่งชี ้) ได้แก่ น.ส.5, ส.2/27, ส.ค.1 และ ส.ป.ก.5-03",
            },
          },
          {
            label: "f. Local government documentation (indicative)",
            text: {
              en: "Land possession documents issued by local government entities (indicative data), such as Land Tax Payment Receipts, P.B.T. 11, Land Survey Documents, and P.D.S. 11.",
              th: "เอกสารสิทธิ์การครอบครองที่ดินที่ออกโดยส่วนท้องถิ่น (มีข้อมูลบ่งชี้) ได้แก่ ใบเสียภาษีที่ดิน, ภ.บ.ท.11, เอกสารรังวัดแปลงที่ดิน และภ.ด.ส.11",
            },
          },
          {
            label: "g. Native or customary land tenure",
            text: {
              en: "Land tenure based on customary practice or local traditional law.",
              th: "การถือครองที่ดินตามประเพณีหรือกฎหมายท้องถิ่น",
            },
          },
          {
            label: "h. Other (please describe)",
            text: {
              en: "Other documents along with a description.",
              th: "อื่นๆ พร้อมคำอธิบาย",
            },
          },
        ],
        note: {
          en: 'If there is no evidence to confirm land use or possession rights, or if the land has no government-issued title document, specify "NA" (Not Applicable).',
          th: "ถ้าหากหลักฐานใดๆเพื่อยืนยันสิทธิ์การใช้ประโยชน์หรือครอบครอง หรือเป็นพื้นที่ที่ไม่มีเอกสารสิทธิ์ ใดๆจากภาครัฐ ให้ระบุว่า “NA” (Not Applicable)",
        },
      },
      {
        no: { en: "4.4.4", th: "2.4" },
        code: "d4_further_mitigation_if_any",
        description: {
          en: 'Further explanation regarding measures to mitigate or prevent issues that arise. If the land genuinely belongs to the farmer and community leaders can confirm actual land use, document "SU-F-009 Certificate of Information for Rubber Farmers on Undocumented Land" can be used as proof of land use. If no issues are found, enter "Not Applicable".',
          th: "อธิบายเพิ่มเติมเกี่ยวกับมาตรการเพื่อลดหรือป้องกันปัญหาที่เกิดขึ้น ในกรณีที่ไม่พบปัญหา ให้ใส่ว่า “Not Applicable”",
        },
      },
      {
        no: { en: "4.4.5", th: "2.5" },
        code: "d5_land_tenure_and_use_rights_final_status",
        description: {
          en: "Results of data analysis from columns d1 – d4 in accordance with Thai laws and EUDR measures:",
          th: "ผลการวิเคราะห์ข้อมูลในคอลัมน์ d1 – d4 ซึ่งจะเป็นไปตามกฎหมายของประเทศไทยและตามมาตรการ EUDR",
        },
        options: [
          {
            label: "a. Official land title or right-of-use",
            text: {
              en: "Possesses legal ownership or land use rights.",
              th: "มีสิทธิ์ในการครอบครองหรือใช้ประโยชน์อย่างถูกต้องตามกฎหมาย",
            },
          },
          {
            label:
              "b. Official land title or right-of-use with illegality screening completed",
            text: {
              en: "Possesses legal ownership or land use rights, with illegal plantations screened out.",
              th: "มีสิทธิ์ในการครอบครองหรือใช้ประโยชน์อย่างถูกต้องตามกฎหมาย ด้วยการคัดกรองสวนยางที่ผิดกฎหมายออกไป",
            },
          },
          {
            label: "c. Official right of use in restricted use area",
            text: {
              en: "Rubber plantation is located in a restricted use area (RA) but has legitimate land use rights.",
              th: "สวนยางอยู่ในพื้นที่จำกัดการใช้ประโยชน์ที่ดิน (RA) และมีสิทธิ์การใช้ประโยชน์อย่างถูกต้องตามกฎหมาย",
            },
          },
          {
            label:
              "d. Local government approval in restricted use area (further mitigation required)",
            text: {
              en: "Approved by local government, but pending complete corrective guidelines.",
              th: "ได้รับอนุมัติจากองค์กรปกครองส่วนท้องถิ ่น แต่ยังต้องรอแนวทางแก้ไขให้ถูกต้องสมบูรณ์",
            },
          },
          {
            label:
              "e. Indicative land tenure with illegality screening completed",
            text: {
              en: "Holder of informal documentation, but verified as non-illegal.",
              th: "ผู้ถือครองที่มีเอกสารไม่เป็นทางการ แต่ได้รับการตรวจสอบว่าไม่ผิดกฎหมาย",
            },
          },
          {
            label:
              "f. Located in protected area or restricted use area without documentation (illegal use)",
            text: {
              en: 'Located in a restricted use area (RA and PA) without land title documents ("NA" in d3_land_tenure_or_documentation).',
              th: "อยู่ในพื้นที่จ ากัดการใช้ประโยชน์ที่ดิน (RA and PA) โดยไม่มีเอกสารสิทธิ์ (“NA” in d3_land_tenure_or_documentation)",
            },
          },
        ],
      },
    ],
  },
  {
    id: "e",
    title: {
      en: "4.5 Environmental Protection",
      th: "3 Environmental Protection",
    },
    description: {
      en: "Explanation of environmental protection checks.",
      th: "อธิบายการตรวจสอบการดูแลรักษาสิ่งแวดล้อม",
    },
    subsections: [
      {
        no: { en: "4.5.1", th: "3.1" },
        code: "e1_plot_located_on_environmentally_non_compliant_area",
        description: {
          en: "Checks rubber plantation plots against natural conservation areas under Thai law, including Watershed Class 1 and 2, and areas with slopes greater than 35%.",
          th: "ตรวจสอบพื้นที่สวนยางพารากับพื้นที่อนุรักษ์ธรรมชาติตามกฎหมายไทย ได้แก่ ลุ่มน้ำลำดับที่ 1 และ 2 และพื้นที่ลาดชันมากกว่า 35%",
        },
      },
      {
        no: { en: "4.5.2", th: "3.2" },
        code: "e2_non_compliant_chemical_use",
        description: {
          en: "Checks chemical usage in rubber plantation plots against Thai regulatory standards:",
          th: "ตรวจสอบการใช้สารเคมีในพื ้นที่สวนยางพาราตามข้อกำาหนดของประเทศไทย",
        },
        options: [
          {
            label: "a. Not detected",
            text: {
              en: "No chemical use detected in rubber plantations",
              th: "ไม่พบการใช้สารเคมีในสวนยางพารา",
            },
          },
          {
            label: "b. Non-compliant chemical use detected",
            text: {
              en: "Non-compliant chemical use detected in rubber plantations under Thai regulations",
              th: "พบการใช้สารเคมีที่ผิดตามข้อกำหนดของประเทศไทยในพื้นที่สวนยางพารา",
            },
          },
        ],
      },
      {
        no: { en: "4.5.3", th: "3.3" },
        code: "e3_environmental_protection_final_status",
        description: {
          en: "Results of data analysis from columns e1 – e2:",
          th: "ผลการวิเคราะห์ข้อมูลในคอลัมน์ e1 – e2",
        },
        options: [
          {
            label: "a. Env non-compliance not detected",
            text: {
              en: "No environmental encroachment or damage detected",
              th: "ไม่พบการบุกรุกหรือทำลายสิ่งแวดล้อม",
            },
          },
          {
            label: "b. Env non-compliance detected",
            text: {
              en: "Environmental encroachment or damage detected",
              th: "พบการบุกรุกหรือทำลายสิ่งแวดล้อม",
            },
          },
        ],
      },
    ],
  },
  {
    id: "f",
    title: {
      en: "4.6 Labor and Human Rights",
      th: "4 Labor and Human Rights",
    },
    description: {
      en: "Checks all labor practices within rubber plantations according to Thai labor laws.",
      th: "ตรวจสอบการใช้แรงงานทั้งหมดภายในพื้นที่สวนยางพาราตามกฎหมายของประเทศไทย",
    },
    subsections: [
      {
        no: { en: "4.6.1", th: "4.1" },
        code: "f1_child_labor",
        description: {
          en: "Checks for child labor under 18 years of age within rubber plantations:",
          th: "ตรวจสอบการใช้แรงงานเด็กอายุต่ำกว่า 18 ปีภายในพื้นที่สวนยางพารา",
        },
        options: [
          {
            label: "a. No child labor detected",
            text: {
              en: "No child labor detected in the area",
              th: "ไม่พบการใช้แรงงานเด็กภายในพื้นที่",
            },
          },
          {
            label:
              "b. Under 18 family work or labor detected but compliant with national and ILO guidelines",
            text: {
              en: "Child labor under 18 detected with ILO compliance; however, as this remains illegal in Thailand, this option is not used.",
              th: "ตรวจพบการใช้แรงงานเด็กอายุต่ำกว่า 18 ปี แต่ได้รับการยินยอมจากองค์การแรงงานระหว่างประเทศ แต่เนื่องจากประเทศไทยยังเป็นเรื่องที่ผิดกฎหมาย จึงไม่มีการใช้คำตอบในข้อนี้",
            },
          },
          {
            label: "c. Non-compliant child labor detected",
            text: {
              en: "Illegal child labor detected",
              th: "ตรวจพบแรงงานเด็กผิดกฎหมาย",
            },
          },
        ],
      },
      {
        no: { en: "4.6.2", th: "4.2" },
        code: "f2_workers",
        description: {
          en: "Checks general labor usage within rubber plantations:",
          th: "ตรวจสอบการใช้แรงงานภายในพื้นที่สวนยางพารา",
        },
        options: [
          {
            label: "a. No workers present",
            text: {
              en: "No workers present in the area",
              th: "ไม่พบการใช้แรงงานในพื้นที่",
            },
          },
          {
            label: "b. Workers present but no non-compliant practices detected",
            text: {
              en: "Workers present with legally compliant practices",
              th: "มีการใช้แรงงานที่ถูกต้องตามกฎหมาย",
            },
          },
          {
            label: "c. Workers present and non-compliant practices detected",
            text: {
              en: "Illegal labor practices detected",
              th: "มีการใช้งานแรงงานผิดกฎหมาย",
            },
          },
        ],
      },
      {
        no: { en: "4.6.3", th: "4.3" },
        code: "f3_forced_or_migrant_labor",
        description: {
          en: "Checks for migrant labor usage within rubber plantations:",
          th: "ตรวจสอบการใช้แรงงานต่างด้าวในพื้นที่สวนยางพารา",
        },
        options: [
          {
            label: "a. No foreign workers and no forced labor detected",
            text: {
              en: "No foreign workers or forced labor detected in the area",
              th: "ไม่พบการใช้แรงงานต่างด้าวหรือการบังคับการใช้แรงงานในพื้นที่",
            },
          },
          {
            label: "b. Foreign workers detected but no forced labor detected",
            text: {
              en: "Foreign workers detected, but no forced labor practices found",
              th: "พบการใช้แรงงานต่างด้าว แต่ไม่พบการบังคับการใช้แรงงานในพื้นที่",
            },
          },
          {
            label: "c. High-risk practices detected",
            text: {
              en: "Practices posing high legal compliance risks detected",
              th: "พบการกระทำาที่เสี่ยงต่อการผิดกฎหมาย",
            },
          },
        ],
      },
      {
        no: { en: "4.6.4", th: "4.4" },
        code: "f4_labor_and_human_rights_final_status",
        description: {
          en: "Results from analyzing labor usage in columns f1 – f3 under EUDR measures:",
          th: "ผลจากการวิเคราะห์การใช้แรงงานในคอลัมน์ f1 – f3 ตามมาตรการของ EUDR",
        },
        options: [
          {
            label: "a. Labor non-compliance not detected",
            text: {
              en: "No illegal labor practices detected",
              th: "ไม่พบการใช้แรงงานที่ผิดกฎหมาย",
            },
          },
          {
            label: "b. Labor non-compliance detected",
            text: {
              en: "Illegal labor practices detected",
              th: "พบการใช้แรงงานผิดกฎหมาย",
            },
          },
        ],
      },
    ],
  },
  {
    id: "g1",
    subsections: [
      {
        no: { en: "4.7", th: "5" },
        code: "g1_fpic_and_land_conflict_with_local_communities",
        description: {
          en: "Checks existing or potential land disputes in rubber plantations, as well as large-scale holdings that may violate Thai land ownership laws:",
          th: "ตรวจสอบข้อพิพาทที่เกิดขึ้นแล้วหรืออาจเกิดขึ้นได้ในพื้นที่สวนยางพารา",
        },
        options: [
          {
            label: "a. No indication of land conflicts",
            text: {
              en: "No land disputes found for the rubber plantation",
              th: "ไม่พบข้อพิพาทของพื้นที่สวนยางพารา",
            },
          },
          {
            label: "b. Land conflicts present",
            text: {
              en: "Land disputes present in the rubber plantation area",
              th: "พื้นที่สวนยางพารามีข้อพิพาท",
            },
          },
        ],
      },
    ],
  },
  {
    id: "h1",
    subsections: [
      {
        no: { en: "4.8", th: "6" },
        code: "h1_trade_tax_and_corruption",
        description: {
          en: "Checks tax payment activity of farmers in rubber plantation areas:",
          th: "ตรวจสอบการเสียภาษีของเกษตรกรในพื ้นที่สวนยางพารา",
        },
        options: [
          {
            label: "a. Evidence or indication of tax activity",
            text: {
              en: "Evidence or indicators of tax payments by farmers",
              th: "มีหลักฐานหรือตัวบ่งชี้ในการเสียภาษีของเกษตรกร",
            },
          },
          {
            label:
              "b. Enabling factors for trade and tax (e.g. receipts or volume monitoring for farmers)",
            text: {
              en: "No evidence or indicators of tax payments by farmers",
              th: "ไม่พบหลักฐานหรือสิ่งที่บ่งชี้ในการเสียภาษีของเกษตรกร",
            },
          },
        ],
      },
    ],
  },
  {
    id: "i1",
    subsections: [
      {
        no: { en: "4.9", th: "7" },
        code: "I1_eudr_status",
        description: {
          en: "Overall analysis result across columns a1 – h1 according to EUDR regulations:",
          th: "ผลการวิเคราะห์ข้อมูลทั ้งหมดในคอลัมน์ a1 – h1 ตามมาตรการของ EUDR",
        },
        options: [
          {
            label: "a. Compliant",
            text: {
              en: "All data is fully compliant with EUDR regulations",
              th: "ข้อมูลทั้งหมดสอดคล้องและเป็นไปตามมาตรการของ EUDR",
            },
          },
          {
            label: "b. Non-compliant",
            text: {
              en: "Data is non-compliant with EUDR regulations",
              th: "ข้อมูลทั้งหมดไม่สอดคล้องและไม่เป็นไปตามมาตรการของ EUDR",
            },
          },
        ],
      },
    ],
  },
];
